import React from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider } from "@/context/AuthContext";
import { CompanyProvider } from "@/context/CompanyContext";
import { PermissionRequestProvider } from "@/context/PermissionRequestContext";
import ProtectedRoute, { AdminRoute, PublicOnlyRoute } from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";
import ErrorBoundary from "@/components/ErrorBoundary";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import VerifyEmail from "@/pages/VerifyEmail";
import Terms from "@/pages/Terms";
import Dashboard from "@/pages/Dashboard";
import Clients from "@/pages/Clients";
import DocumentsList from "@/pages/DocumentsList";
import DocumentBuilder from "@/pages/DocumentBuilder";
import TemplatesGallery from "@/pages/TemplatesGallery";
import ClientStatement from "@/pages/ClientStatement";
import Admin from "@/pages/Admin";
import AdminAudit from "@/pages/AdminAudit";
import AdminActivity from "@/pages/AdminActivity";
import AdminSettings from "@/pages/AdminSettings";
import Companies from "@/pages/Companies";
import Settings from "@/pages/Settings";
import CookieBanner from "@/components/CookieBanner";

/**
 * Keying on the pathname remounts the boundary whenever the route changes, so
 * a crash is cleared by navigating away — including via browser back/forward,
 * which never touches the boundary's own buttons.
 */
function RouteErrorBoundary({ children }) {
  const location = useLocation();
  return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/terms" element={<Terms />} />

          {/* Auth screens: signed-in users are redirected to the app. */}
          <Route
            path="/login"
            element={<PublicOnlyRoute><Login /></PublicOnlyRoute>}
          />
          <Route
            path="/register"
            element={<PublicOnlyRoute><Register /></PublicOnlyRoute>}
          />
          <Route
            path="/verify-email"
            element={<PublicOnlyRoute><VerifyEmail /></PublicOnlyRoute>}
          />

          {/* Every panel below this point requires a validated JWT. */}
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <CompanyProvider>
                  <PermissionRequestProvider>
                    {/* A render fault in one page must not unmount
                        AuthProvider and drop the user back at /login. */}
                    <RouteErrorBoundary>
                      <AppLayout />
                    </RouteErrorBoundary>
                  </PermissionRequestProvider>
                </CompanyProvider>
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="admin" element={<AdminRoute><Admin /></AdminRoute>} />
            <Route
              path="admin/audit"
              element={<AdminRoute><AdminAudit /></AdminRoute>}
            />
            <Route path="companies" element={<Companies />} />
            {/* Legacy deep link kept working; now redirects to the multi-company page. */}
            <Route path="company" element={<Navigate to="/app/companies" replace />} />
            <Route path="settings" element={<Settings />} />
            <Route path="clients" element={<Clients />} />
            <Route path="templates" element={<TemplatesGallery />} />

            <Route path="invoices" element={<DocumentsList />} />
            <Route path="receipts" element={<DocumentsList />} />
            <Route path="purchase-orders" element={<DocumentsList />} />
            <Route path="notes" element={<DocumentsList />} />

            <Route
              path="admin/activity"
              element={<AdminRoute><AdminActivity /></AdminRoute>}
            />
            <Route
              path="admin/settings"
              element={<AdminRoute><AdminSettings /></AdminRoute>}
            />

            <Route path="clients/:id/statement" element={<ClientStatement />} />

            <Route path="documents/new/:type" element={<DocumentBuilder />} />
            <Route path="documents/:id" element={<DocumentBuilder />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <CookieBanner />
      </BrowserRouter>
      <Toaster position="top-center" richColors closeButton />
    </AuthProvider>
  );
}

export default App;